import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { HeaderPortal } from "../../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import Skeleton from "../../../components/ui-components/Skeleton";
import { toast } from "../../../components/ui-components/Toast";
import { listUsers } from "../../users/api";
import { useRoles, useCapabilityCatalog, useControlCheckMutation, useCreateOrgRoleMutation } from "../api";
import { slugify, friendlyRoleError } from "../roleFormUtils";
import { SENSITIVE_CAP, WIZARD_STEPS } from "./constants";
import StepNamePurpose from "./StepNamePurpose";
import StepWhatDiffers from "./StepWhatDiffers";
import StepReviewCapabilities from "./StepReviewCapabilities";
import StepGovernance from "./StepGovernance";
import StepControlCheck from "./StepControlCheck";

const STEP_KEYS = WIZARD_STEPS.map((s) => s.key);

// The create-from-template wizard (Sub-project E2, Task 6) — replaces
// CustomRoleEditor's old CREATE mode at /settings/roles/new (CustomRoleEditor
// keeps handling view/edit of an EXISTING role at /settings/roles/:id).
// Reached either seeded from a template (RoleCard's/RolesCatalogPage's
// "Use template" -> location.state.cloneFrom) or from scratch
// (RolesCatalogPage's "New organisation role", no state). See
// docs/superpowers/specs/2026-07-26-governed-roles-e2-design.md §3 for the
// step-by-step spec this mirrors.
export default function OrgRoleWizard() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const rolesQ = useRoles({ includeHidden: true });
  const capsQ = useCapabilityCatalog();
  const usersQ = useQuery(["users-picker-for-role-owner"], () => listUsers({ limit: 200 }));

  const roles = useMemo(() => rolesQ.data || [], [rolesQ.data]);
  const capabilityCatalog = capsQ.data || [];
  const isLoading = rolesQ.isLoading || capsQ.isLoading;

  const templateOptions = useMemo(() => roles.filter((r) => r.type === "template"), [roles]);

  const [stepIndex, setStepIndex] = useState(0);
  const stepKey = STEP_KEYS[stepIndex];

  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [cloneCode, setCloneCode] = useState(location.state?.cloneFrom || "");
  const [whatDiffers, setWhatDiffers] = useState(null);
  const [selectedCaps, setSelectedCaps] = useState([]);

  const [owner, setOwner] = useState("");
  const [reviewFrequency, setReviewFrequency] = useState("365");
  const [includesSensitive, setIncludesSensitive] = useState(false);
  const [sensitiveManuallyEdited, setSensitiveManuallyEdited] = useState(false);
  const [isScoped, setIsScoped] = useState(false);
  const [defaultAuthority, setDefaultAuthority] = useState([]);

  const [findings, setFindings] = useState([]);
  const [checkError, setCheckError] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const cloneSourceRole = useMemo(
    () => (cloneCode ? roles.find((r) => r.code === cloneCode) : null),
    [roles, cloneCode]
  );
  const baselineCaps = useMemo(() => cloneSourceRole?.capabilities || [], [cloneSourceRole]);

  // Re-seed the proposed capability set whenever the chosen template
  // changes (including "start from scratch", cloneCode === "") — but only
  // then, so toggles made in step 3 survive navigating back and forth
  // between steps without changing the template.
  useEffect(() => {
    setSelectedCaps(baselineCaps ? [...baselineCaps] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneCode, cloneSourceRole]);

  // includes_sensitive auto-detects from the selected capabilities until the
  // admin explicitly overrides it on the governance step (mirrors
  // CustomRoleEditor's codeManuallyEdited pattern).
  useEffect(() => {
    if (!sensitiveManuallyEdited) setIncludesSensitive(selectedCaps.includes(SENSITIVE_CAP));
  }, [selectedCaps, sensitiveManuallyEdited]);

  const toggleCap = (code) => {
    setSelectedCaps((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const userOptions = useMemo(() => {
    const raw = usersQ.data?.users?.items || [];
    return raw.map((u) => ({
      value: String(u.id ?? u.ID),
      label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || String(u.id ?? u.ID ?? ""),
    }));
  }, [usersQ.data]);

  const controlCheckMut = useControlCheckMutation({
    onSuccess: (data) => {
      setFindings(data || []);
      setCheckError(false);
    },
    onError: () => setCheckError(true),
  });

  const invalidateRoles = () => queryClient.invalidateQueries({ queryKey: ["roles"] });

  const createOrgRoleMut = useCreateOrgRoleMutation({
    onSuccess: (created) => {
      invalidateRoles();
      toast.success("Role created");
      navigate(created?.id ? `/settings/roles/${created.id}` : "/settings/roles");
    },
    onError: (err) => {
      const code = err?.serverBody?.error?.code;
      if (code === "ROLE_CONTROL_CHECK_FAILED") {
        // Stay on the control-check step (it's already the only step this
        // action fires from) and refresh findings from the authoritative
        // server-side re-check rather than the FE's earlier preview.
        setFindings(err.serverBody.error.findings || []);
      }
      toast.error(friendlyRoleError(err, "create"));
    },
  });

  const hasCritical = findings.some((f) => f.severity === "critical");

  // Run the control-check on entering that step (spec §3.5: "re-run on step
  // entry"), against whatever the admin has configured so far.
  useEffect(() => {
    if (stepKey !== "control") return;
    setCheckError(false);
    controlCheckMut.mutate({
      caps: selectedCaps,
      hasApprovalAuthority: defaultAuthority.some((r) => r.object_type && r.max_amount !== "" && r.max_amount != null),
      isScoped,
      targetUserIds: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  const canGoNext = () => {
    if (stepKey === "name") return name.trim().length > 0 && purpose.trim().length > 0;
    if (stepKey === "differs") return !!whatDiffers && whatDiffers !== "scope_only";
    if (stepKey === "capabilities") return true;
    if (stepKey === "governance") return owner !== "";
    return false;
  };

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEP_KEYS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const buildPayload = (status) => {
    const reviewDays = reviewFrequency === "none" ? null : Number(reviewFrequency);
    const payload = {
      code: slugify(name) || `role_${Date.now()}`,
      name: name.trim(),
      purpose: purpose.trim() || undefined,
      cloned_from: cloneCode || undefined,
      capabilities: selectedCaps,
      status,
      owner_user_id: owner ? Number(owner) : undefined,
      review_frequency_days: reviewDays,
      includes_sensitive: includesSensitive,
      is_scoped: isScoped,
      default_authority: defaultAuthority
        .filter((r) => r.object_type && r.max_amount !== "" && r.max_amount != null)
        .map((r) => ({ object_type: r.object_type, max_amount_cents: Math.round(Number(r.max_amount) * 100) })),
    };
    if (status === "active" && hasCritical) payload.override_reason = overrideReason.trim();
    return payload;
  };

  const handleSaveDraft = () => createOrgRoleMut.mutate(buildPayload("draft"));
  const handleActivate = () => createOrgRoleMut.mutate(buildPayload("active"));

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={320} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            Create organisation role
          </div>
        </div>
      </HeaderPortal>

      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/roles")}>
            Cancel
          </button>
          {stepIndex > 0 && (
            <button type="button" className={secondaryButtonCls} onClick={goBack}>
              Back
            </button>
          )}
          {stepKey !== "control" && (
            <button type="button" className={primaryButtonCls} onClick={goNext} disabled={!canGoNext()}>
              Next
            </button>
          )}
        </div>
      </HeaderPortal>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[680px] space-y-4">
          <div className="text-xs font-medium text-text-3 uppercase tracking-[0.08em]">
            Step {stepIndex + 1} of {STEP_KEYS.length} · {WIZARD_STEPS[stepIndex].label}
          </div>

          {stepKey === "name" && (
            <StepNamePurpose
              name={name}
              setName={setName}
              purpose={purpose}
              setPurpose={setPurpose}
              cloneCode={cloneCode}
              setCloneCode={setCloneCode}
              templateOptions={templateOptions}
            />
          )}

          {stepKey === "differs" && (
            <StepWhatDiffers
              whatDiffers={whatDiffers}
              setWhatDiffers={setWhatDiffers}
              cloneCode={cloneCode}
              cloneSourceRoleName={cloneSourceRole?.name}
            />
          )}

          {stepKey === "capabilities" && (
            <StepReviewCapabilities
              capabilityCatalog={capabilityCatalog}
              baselineCaps={baselineCaps}
              selectedCaps={selectedCaps}
              toggleCap={toggleCap}
            />
          )}

          {stepKey === "governance" && (
            <StepGovernance
              userOptions={userOptions}
              owner={owner}
              setOwner={setOwner}
              reviewFrequency={reviewFrequency}
              setReviewFrequency={setReviewFrequency}
              includesSensitive={includesSensitive}
              setIncludesSensitive={(v) => {
                setSensitiveManuallyEdited(true);
                setIncludesSensitive(v);
              }}
              isScoped={isScoped}
              setIsScoped={setIsScoped}
              defaultAuthority={defaultAuthority}
              setDefaultAuthority={setDefaultAuthority}
            />
          )}

          {stepKey === "control" && (
            <StepControlCheck
              isChecking={controlCheckMut.isPending}
              findings={findings}
              checkError={checkError}
              overrideReason={overrideReason}
              setOverrideReason={setOverrideReason}
              onSaveDraft={handleSaveDraft}
              onActivate={handleActivate}
              isSaving={createOrgRoleMut.isPending}
            />
          )}
        </div>
      </div>
    </div>
  );
}
